import type { BuildingRules } from './building.js';
import type { PowerState } from './rules/powers.js';
import type { Loadout } from './upgrades.js';

/** Ground floor is 0. A level with `floors: 8` has valid indices 0..7. */
export type FloorIndex = number;

export type PassengerId = string;

/**
 * How the player boards passengers. Both modes ship in v0.1 so the same build
 * can A/B the core interaction hypothesis — see docs/DECISIONS.md ADR-0003.
 *
 * - `dispatch`: the player only routes the car. Waiting passengers board
 *   automatically in arrival order until the cabin is full.
 * - `select`: the player routes the car AND taps each passenger to board them.
 */
export type InteractionMode = 'dispatch' | 'select';

export type PassengerState = 'unspawned' | 'waiting' | 'riding' | 'delivered' | 'abandoned';

/**
 * What makes a passenger interesting, expressed as independent traits rather
 * than as a class hierarchy.
 *
 * An "archetype" is just a named bundle of these, defined in content. That keeps
 * variety combinatorial: a courier who also carries luggage costs a JSON entry,
 * not a new code path. Every trait here has to create a *conflict* — a trait
 * that only changes an icon is a cosmetic variation and does not belong.
 */
export interface PassengerTraits {
  /** Capacity units consumed. 2 models someone travelling with luggage. */
  readonly size: number;
  /** Gives up and leaves this long after appearing. Null means they wait forever. */
  readonly patienceMs: number | null;
  /**
   * Must be delivered by this time, measured from the start of the level.
   *
   * The clock keeps running after they board, so a courier can be lost from
   * inside the cabin. That is the whole trait: taking on a delivery you cannot
   * finish is a mistake the player is meant to see coming.
   */
  readonly deadlineMs: number | null;
  /** Scores on how fast they are served. Rewarding, and often inconvenient. */
  readonly vip: boolean;
  /**
   * Passengers sharing a group id must travel together: they board all at once
   * or not at all, so a group of two cannot be split across trips.
   */
  readonly groupId: string | null;
  /**
   * Losing them ends the run, there and then (ADR-0029): an emergency crew, or
   * the person they came for. On a floor they board before anyone the player
   * has not marked first — when they fit.
   */
  readonly critical: boolean;
  /**
   * They ride alone (ADR-0029): an arrest. They board only an empty cabin, and
   * nobody else boards while they are in it.
   */
  readonly alone: boolean;
  /**
   * A deadline counted from their own arrival instead of from the start of the
   * level. It becomes `deadlineMs` when the run starts; a call-out's return
   * inherits its crew's.
   */
  readonly withinMs: number | null;
  /**
   * A call-out (ADR-0029). Once delivered they are at the incident for
   * `stayMs`, then come back as `back` — the crew and who they came for — to
   * the floor they came from, on the same clock. The return is its own spawn,
   * written by the traffic that sends them.
   */
  readonly callout: { readonly back: string; readonly stayMs: number } | null;
  /**
   * Places they take on a landing while they wait (ADR-0031). A hotel corridor
   * that holds three holds a housekeeping cart and one guest.
   */
  readonly landingSpace: number;
  /**
   * They work here (ADR-0031): never turned away from a full landing. A cart
   * that arrives anyway is how the corridor fills up and guests turn away.
   */
  readonly staff: boolean;
  /** Display name for the bundle, e.g. "courier". Presentation and analytics only. */
  readonly archetype: string;
}

export const DEFAULT_TRAITS: PassengerTraits = {
  size: 1,
  patienceMs: null,
  deadlineMs: null,
  vip: false,
  groupId: null,
  critical: false,
  alone: false,
  withinMs: null,
  callout: null,
  landingSpace: 1,
  staff: false,
  archetype: 'normal',
};

export interface Passenger extends PassengerTraits {
  readonly id: PassengerId;
  readonly origin: FloorIndex;
  readonly destination: FloorIndex;
  /**
   * Simulation time at which this passenger appears on their origin floor. For
   * one who follows another (`follows`), unknown until that one is delivered.
   */
  spawnMs: number;
  /**
   * Appears `followDelayMs` after this passenger is delivered, rather than at a
   * set time (ADR-0029): a call-out's return, after its crew. Null for everyone
   * who simply arrives.
   */
  readonly follows: PassengerId | null;
  readonly followDelayMs: number;
  state: PassengerState;
  /**
   * Where this passenger is standing right now.
   *
   * Equal to `origin` for the whole of their journey in an ordinary building.
   * It only diverges when an interchange makes them get out partway and wait
   * again — `origin` stays what it always was, which is what the results screen
   * and every tool mean by it.
   */
  standingFloor: FloorIndex;
  /**
   * When the current wait began: their arrival, or the moment they stepped out
   * at an interchange. Patience is measured from here, so somebody who has just
   * been carried halfway does not give up the instant they land.
   */
  waitingSinceMs: number;
  /** True once the first leg of an interchange journey is done. */
  transferred: boolean;
  /**
   * The door cycle from which this passenger may board.
   *
   * Exists for one case: somebody who has just stepped out at an interchange is
   * standing next to an open door they are entitled to walk back through, which
   * would turn the whole rule into a few hundred milliseconds of transfer time
   * and change nothing. Making them wait for the car to come back is what turns
   * a crossing journey into two journeys, and what makes the transfer floor fill
   * up with traffic the player put there.
   */
  boardableFromCycle: number;
  /**
   * Floors the car must open at, with them aboard, before they get out
   * (ADR-0031): a postman's rounds. Each is crossed off as the doors open
   * there; they get out at their destination only once none is left.
   */
  errands: FloorIndex[];
  /** Set when the passenger steps into the cabin. */
  boardedMs: number | null;
  /** Set when the passenger steps out at their destination. */
  deliveredMs: number | null;
  /** Set when patience ran out and they walked away. */
  abandonedMs: number | null;
  /** Why they were lost, so the results screen can say which clock ran out. */
  lostReason: 'gave-up' | 'deadline' | 'no-room' | null;
}

export type DoorPhase = 'closed' | 'opening' | 'open' | 'closing';

export type ElevatorPhase = 'idle' | 'moving' | 'servicing';

export interface Elevator {
  /** Position in MILLI_PER_FLOOR units. 2500 means "halfway between floor 2 and 3". */
  positionMilli: number;
  phase: ElevatorPhase;
  doorPhase: DoorPhase;
  /** Floor the car is travelling to. Null when idle or servicing. */
  targetFloor: FloorIndex | null;
  /** Counts down the current door phase / transfer slot, in ms. */
  phaseTimerMs: number;
  /** Counts up while doors are open, to enforce minimum and maximum dwell. */
  dwellMs: number;
  riders: PassengerId[];
  /** Sum of `size` over riders. Kept alongside `riders` to keep checks O(1). */
  load: number;
  /**
   * Passengers the player tapped in `select` mode, in tap order. They board one
   * per transfer slot, so a tap registers instantly even though boarding costs time.
   * Always empty in `dispatch` mode.
   */
  boardQueue: PassengerId[];
  /**
   * Waiting passengers the player marked to go first (ADR-0025), on any floor,
   * the latest first. In `dispatch` mode they board before anyone else on
   * their floor; when everyone fits it changes nothing. A marked passenger
   * leaves the list as they board. Always empty in `select`.
   *
   * Not hashed: its only effect is who boards, and that is.
   */
  priority: PassengerId[];
  /**
   * Squeeze in (ADR-0055): 0 none; 1 used, for the next stop; 2 at that stop,
   * two seats more until the car leaves it. Not hashed: who boards is.
   */
  squeeze: 0 | 1 | 2;
  /**
   * Floors to go to after `targetFloor`, in the order they were tapped
   * (`addStop`). The car takes the first once it is done where it is. Always
   * empty for a run that only ever sends `callTo`, and hashed only when not,
   * so every replay recorded before it hashes as it did.
   */
  stops: FloorIndex[];
  /** Set by `closeDoors`, or by routing elsewhere while the doors are open. */
  closeRequested: boolean;
  /**
   * Where the current run began, so the cable knows when the car is far enough
   * along to speed up (ADR-0033). Not hashed: it only ever moves the car, and
   * the car's position is.
   */
  runFromMilli: number;
}

export interface ElevatorSpec {
  /** Maximum summed passenger `size` the cabin holds. */
  readonly capacity: number;
  /** Travel time for one floor, in ms. Lower is faster. */
  readonly msPerFloor: number;
  readonly doorOpenMs: number;
  readonly doorCloseMs: number;
  /** Time cost of one passenger stepping in or out. Makes boarding choices cost time. */
  readonly transferMs: number;
  /** Doors stay open at least this long, so arrivals are readable. */
  readonly minDwellMs: number;
  /** Doors close on their own after this long, so a level cannot stall forever. */
  readonly maxDwellMs: number;
  readonly startFloor: FloorIndex;
  /**
   * The cable (ADR-0033): the share of the motor's time a floor that a run
   * saves once it is past its first floor, in thousandths. A share, not a
   * number of milliseconds, so a better rope helps whatever motor it hangs
   * from. Zero on every stock lift, where a run is the motor's speed all the
   * way.
   */
  readonly longRunSavingPermille: number;
}

export type RunStatus = 'playing' | 'won' | 'lost';

/**
 * What a level grades on.
 *
 * - `time`: elapsed milliseconds, lower is better. Enough on its own while every
 *   passenger is interchangeable, because time already folds in route order and
 *   dwell discipline.
 * - `service`: the service score, higher is better. Once passengers differ in
 *   urgency and value, time stops capturing whether the *right* choices were
 *   made — serving a VIP first can cost seconds and still be correct.
 * - `trips`: how many times a car set off, lower is better (ADR-0037). A
 *   puzzle shift's measure: no clock to beat, only a plan to make.
 */
export type StarMetric = 'time' | 'service' | 'trips';

export interface StarThresholds {
  readonly metric?: StarMetric;
  readonly three: number;
  readonly two: number;
}

export interface ScoreState {
  delivered: number;
  /** Total spawn-to-board time across delivered and riding passengers. */
  totalWaitMs: number;
  /** Total board-to-deliver time across delivered passengers. */
  totalRideMs: number;
  /** Cumulative distance travelled by the car, in MILLI_PER_FLOOR units. */
  distanceMilli: number;
  doorCycles: number;
  /** Gave up and left. Each one is a decision the player made, by omission. */
  abandoned: number;
  /** Deliveries made before the deadline, and jobs lost to it. */
  couriersOnTime: number;
  couriersLate: number;
  vipsServed: number;
  /**
   * First legs completed at an interchange. Not deliveries — a transfer is work
   * done that has not paid yet, and counting it as a delivery would let a player
   * "finish" a level by shuttling everyone to the transfer floor and stopping.
   */
  transfers: number;
  /**
   * Each time a car sets off for a floor (ADR-0037): a puzzle shift's limit
   * and what it is graded on. Counted on every run, read only by those, and
   * not hashed — a run's trips follow from its commands, and its outcome is.
   */
  trips: number;
  /**
   * The service score, accumulated as the run happens rather than computed at
   * the end, so a partial run is still scored honestly. See `scoring.ts`.
   */
  service: number;
}

export interface GameState {
  readonly levelId: string;
  readonly seed: number;
  readonly mode: InteractionMode;
  /** Whole ticks elapsed. Wall time is `tick * TICK_MS`. */
  tick: number;
  elapsedMs: number;
  readonly floorCount: number;
  readonly spec: ElevatorSpec;
  /** Structural rules of the building this level sits in. Empty for a plain tower. */
  readonly building: BuildingRules;
  /** The hardware this run was played with. `spec` is the level's cabin after it. */
  readonly loadout: Loadout;
  readonly stars: StarThresholds;
  /** Run ends as `lost` if this is exceeded before the objective is met. */
  readonly timeLimitMs: number;
  /** Derived once at level load so per-tick movement stays integer. */
  readonly speedMilliPerTick: number;
  /** The speed past a run's first floor, with the cable's share (ADR-0033). Equal without one. */
  readonly longRunMilliPerTick: number;
  /** The car, or the first car of the bank. */
  elevator: Elevator;
  /**
   * The rest of the bank (ADR-0034), in order: car 1, car 2… Empty on every
   * level with one car, which is every level before it, and then nothing about
   * a run changes. Each runs by the same rules as `elevator`, after it.
   */
  extraCars: Elevator[];
  /** Insertion-ordered. Order is part of the deterministic contract. */
  passengers: Passenger[];
  score: ScoreState;
  /**
   * Power-ups (ADR-0055): the meter, the charge, what was used last. Not
   * hashed: what a power does shows in what is (who boards, who is home).
   */
  power: PowerState;
  status: RunStatus;
  rngState: number;
  /** Populated when the run ends, so the UI can explain the outcome. */
  endReason: string | null;
}
